import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ChevronDown } from 'lucide-react';

/** Group → accent color palette (matches Tips categories) */
const GROUP_META = {
  filler: { accent: '#8948C3', accentSoft: 'rgba(137,72,195,0.10)' },
  botox: { accent: '#40D3BE', accentSoft: 'rgba(64,211,190,0.14)' },
};
const GROUP_ORDER = ['filler', 'botox'];
const VISIBLE_COUNT = 4;
const EASE = [0.16, 1, 0.3, 1];

function FaqItem({ item, index, accent, isAr, open, onToggle }) {
  return (
    <div className="border-b border-[#10092E]/10" data-testid={`faq-item-${index}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`faq-question-${index}`}
        aria-expanded={open}
        className="group w-full flex items-center justify-between gap-4 py-5 md:py-6 text-start"
      >
        <span
          className={`text-lg md:text-xl font-bold text-[#10092E] transition-colors duration-300 group-hover:text-[var(--faq-accent)] ${
            isAr ? 'font-ar' : 'font-en'
          }`}
          style={{ '--faq-accent': accent }}
        >
          {item.q}
        </span>
        <span
          className="shrink-0 inline-flex w-9 h-9 md:w-10 md:h-10 rounded-full items-center justify-center border transition-all duration-300"
          style={{
            borderColor: open ? accent : 'rgba(16,9,46,0.15)',
            background: open ? accent : 'transparent',
            color: open ? '#fff' : '#10092E',
          }}
        >
          <Plus
            size={16}
            className={`transition-transform duration-300 ${open ? 'rotate-45' : ''}`}
          />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <p
              data-testid={`faq-answer-${index}`}
              className={`pb-6 md:pb-7 pe-2 md:pe-14 max-w-2xl text-[#10092E]/65 leading-relaxed text-base md:text-lg ${
                isAr ? 'font-ar' : 'font-en'
              }`}
            >
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [expanded, setExpanded] = useState(false);
  const [openKey, setOpenKey] = useState(0);
  const groups = t('faq.groups', { returnObjects: true }) || {};

  let counter = 0;

  return (
    <section id="faq" data-testid="faq-section" className="relative bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32">
        <div
          className={`text-[14px] md:text-[15px] text-[#8948C3] mb-6 font-semibold ${
            isAr ? '' : 'tracking-[0.32em] uppercase'
          }`}
          data-testid="faq-eyebrow"
        >
          {t('faq.eyebrow')}
        </div>
        <h2
          className={`text-[#10092E] font-bold tracking-tight leading-[1.05] ${
            isAr ? 'font-ar text-5xl md:text-7xl' : 'font-en text-5xl md:text-7xl'
          }`}
          data-testid="faq-heading"
        >
          {t('faq.heading')}
        </h2>
        <p
          className={`mt-6 max-w-2xl text-[#10092E]/60 leading-relaxed ${
            isAr ? 'font-ar text-base md:text-lg' : 'font-en text-base md:text-lg'
          }`}
        >
          {t('faq.subheading')}
        </p>
        <div className="hairline mt-14" />
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          {GROUP_ORDER.map((gKey) => {
            const group = groups[gKey];
            if (!group) return null;
            const meta = GROUP_META[gKey];
            const items = Array.isArray(group.items) ? group.items : [];
            const groupStart = counter;
            counter += items.length;
            const visibleItems = expanded
              ? items
              : items.slice(0, Math.max(0, VISIBLE_COUNT - groupStart));
            if (visibleItems.length === 0) return null;
            const isExtraGroup = groupStart >= VISIBLE_COUNT;

            return (
              <motion.div
                key={gKey}
                className="mb-14 last:mb-0"
                initial={isExtraGroup ? { opacity: 0, y: 24 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
              >
                <span
                  className={`inline-flex items-center rounded-full px-4 py-1.5 text-xs md:text-sm font-bold mb-3 ${
                    isAr ? 'font-ar' : 'font-en tracking-wide uppercase'
                  }`}
                  style={{ background: meta.accentSoft, color: meta.accent }}
                  data-testid={`faq-group-${gKey}`}
                >
                  {group.label}
                </span>
                {visibleItems.map((item, i) => {
                  const idx = groupStart + i;
                  const isExtra = idx >= VISIBLE_COUNT;
                  return (
                    <motion.div
                      key={idx}
                      initial={isExtra ? { opacity: 0, y: 20 } : false}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.6,
                        delay: isExtra ? (idx - VISIBLE_COUNT) * 0.05 : 0,
                        ease: EASE,
                      }}
                    >
                      <FaqItem
                        item={item}
                        index={idx}
                        accent={meta.accent}
                        isAr={isAr}
                        open={openKey === idx}
                        onToggle={() => setOpenKey(openKey === idx ? null : idx)}
                      />
                    </motion.div>
                  );
                })}
              </motion.div>
            );
          })}
        </motion.div>

        <div className="mt-12 md:mt-16 text-center">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="faq-toggle-more"
            className={`group inline-flex items-center gap-3 rounded-full border border-[#8948C3]/30 bg-white px-8 py-4 text-sm md:text-base font-bold text-[#8948C3] shadow-[0_14px_40px_-16px_rgba(137,72,195,0.45)] transition-colors duration-300 hover:bg-[#8948C3] hover:text-white ${
              isAr ? 'font-ar' : 'font-en'
            }`}
          >
            {expanded ? t('faq.less') : t('faq.more')}
            <ChevronDown
              size={18}
              className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>
    </section>
  );
}
